import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import {
  CubeIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ArrowsRightLeftIcon,
  BellAlertIcon,
  ShieldCheckIcon,
  CloudArrowUpIcon,
  DevicePhoneMobileIcon,
  CurrencyDollarIcon,
  PrinterIcon,
  ReceiptPercentIcon,
  QrCodeIcon,
  TruckIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  BanknotesIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';

const modules = [
  {
    id: 'inventory',
    label: 'Inventory',
    color: 'from-primary-600 to-primary-800',
    accent: 'primary',
    icon: CubeIcon,
    tagline: 'Total Stock Control',
    headline: 'Manage Every Product, Every Location, in Real Time',
    description:
      'From a single shop to a multi-warehouse enterprise — StockWise gives you live visibility into every unit of stock across your entire operation.',
    bullets: [
      'Unlimited products with categories, images & specifications',
      'Multi-warehouse stock levels with real-time sync',
      'Inter-warehouse transfer tracking with full audit trail',
      'Automated low stock alerts via email & Telegram',
      'Barcode & QR code generation and scanning',
      'SKU auto-generation and variant management',
      'Lot/batch tracking with expiry date monitoring',
      'Composite items and bundle kits support',
    ],
    subFeatures: [
      { icon: QrCodeIcon, name: 'Barcode & QR Codes', desc: 'Generate and print labels instantly for any product.' },
      { icon: BuildingStorefrontIcon, name: 'Multi-Warehouse', desc: 'Separate stock levels per location with transfer logs.' },
      { icon: BellAlertIcon, name: 'Low Stock Alerts', desc: 'Get notified before you run out — on Telegram or email.' },
      { icon: ArrowsRightLeftIcon, name: 'Lot & Batch Tracking', desc: 'Track expiry dates, batches, and lot numbers.' },
    ],
  },
  {
    id: 'orders',
    label: 'Orders',
    color: 'from-indigo-600 to-indigo-900',
    accent: 'indigo',
    icon: DocumentTextIcon,
    tagline: 'End-to-End Order Management',
    headline: 'Sales & Purchase Orders, Start to Finish',
    description:
      'Handle the full lifecycle of every order — create, approve, fulfill, invoice, and track payment — without switching tools.',
    bullets: [
      'Sales orders with customer management and pricing',
      'Purchase orders with supplier and warehouse assignment',
      'PDF invoice generation with company branding',
      'Send orders via Email or WhatsApp directly from the app',
      'Payment recording with proof of payment upload',
      'Sales returns and purchase returns tracking',
      'Stock auto-deducted on fulfillment',
      'Order status workflow: Draft → Confirmed → Fulfilled',
    ],
    subFeatures: [
      { icon: PrinterIcon, name: 'PDF Invoices', desc: 'Branded, professional invoices in one click.' },
      { icon: TruckIcon, name: 'Fulfillment Tracking', desc: 'Mark items as delivered and auto-update stock.' },
      { icon: BanknotesIcon, name: 'Payment Recording', desc: 'Record partial or full payments with bank details.' },
      { icon: ArrowsRightLeftIcon, name: 'Returns Management', desc: 'Handle sales and purchase returns seamlessly.' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    color: 'from-emerald-600 to-emerald-900',
    accent: 'emerald',
    icon: BanknotesIcon,
    tagline: 'Built-In Finance Module',
    headline: 'A Complete Finance System, Integrated with Your Inventory',
    description:
      'No need for a separate accounting tool. StockWise has accounts receivable, accounts payable, general ledger, and financial reports all built in.',
    bullets: [
      'Accounts Receivable — track all customer debts',
      'Accounts Payable — manage supplier payments',
      'General Ledger with chart of accounts',
      'Journal entries with double-entry bookkeeping',
      'Tax configuration (VAT, withholding tax)',
      'Profit & Loss, Cash Flow, and Balance Sheet reports',
      'Inventory valuation reports',
      'AR/AP auto-sync with sales and purchase orders',
    ],
    subFeatures: [
      { icon: ReceiptPercentIcon, name: 'Tax Configuration', desc: 'Set up VAT and withholding tax rates per item.' },
      { icon: BookOpenIcon, name: 'General Ledger', desc: 'Full double-entry bookkeeping with journal entries.' },
      { icon: ChartBarIcon, name: 'Financial Reports', desc: 'P&L, Cash Flow, Balance Sheet — exportable to PDF.' },
      { icon: CurrencyDollarIcon, name: 'Multi-Currency', desc: 'Manage transactions in ETB, USD, EUR and more.' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    color: 'from-orange-600 to-orange-900',
    accent: 'orange',
    icon: ChartBarIcon,
    tagline: 'Data-Driven Decisions',
    headline: 'Reports That Tell the Full Story of Your Business',
    description:
      'StockWise gives you a complete reporting suite — from inventory movement to sales performance — all exportable with one click.',
    bullets: [
      'Stock movement and stock valuation reports',
      'Sales analysis by product, customer, and period',
      'Purchase analysis by supplier and product',
      'Reorder alerts and turnover analysis',
      'Warehouse-level inventory reports',
      'Revenue, payment, and outstanding balance reports',
      'Export to PDF or Excel',
      'Date range filters on every report',
    ],
    subFeatures: [
      { icon: ChartBarIcon, name: 'Sales Reports', desc: 'Revenue, payments, outstanding, and customer reports.' },
      { icon: BuildingStorefrontIcon, name: 'Inventory Reports', desc: 'Stock valuation, movement, and reorder alerts.' },
      { icon: DocumentTextIcon, name: 'Purchase Reports', desc: 'Spending, receiving, and supplier analysis.' },
      { icon: PrinterIcon, name: 'Export PDF / Excel', desc: 'Download any report in the format you need.' },
    ],
  },
  {
    id: 'team',
    label: 'Team & Security',
    color: 'from-rose-600 to-rose-900',
    accent: 'rose',
    icon: UserGroupIcon,
    tagline: 'Secure Collaboration',
    headline: 'Role-Based Access, Audit Trails, and Enterprise Security',
    description:
      'Give every team member exactly the access they need — no more, no less — with a full audit trail of every action.',
    bullets: [
      'Role-based user permissions (Admin, Manager, Staff)',
      'Full audit logs — who changed what and when',
      'Multi-tenant architecture with data isolation',
      'Subdomain per company (yourcompany.stockwise.app)',
      'Cloud-hosted with 99.9% uptime',
      'Automated daily backups',
      'Mobile-responsive on all screen sizes',
      'Telegram bot notifications per user',
    ],
    subFeatures: [
      { icon: ShieldCheckIcon, name: 'Role Permissions', desc: 'Granular control over every module and action.' },
      { icon: DocumentTextIcon, name: 'Audit Logs', desc: 'Complete trail of all user actions in the system.' },
      { icon: CloudArrowUpIcon, name: 'Cloud Backup', desc: 'Automatic daily backups with zero downtime.' },
      { icon: DevicePhoneMobileIcon, name: 'Mobile Ready', desc: 'Fully responsive — works on phone, tablet, desktop.' },
    ],
  },
];

const accentClasses = {
  primary: {
    tag: 'bg-primary-500/20 text-primary-400 border-primary-500/30',
    bullet: 'text-primary-400',
    subCard: 'border-primary-500/20 hover:border-primary-500/50 hover:shadow-primary-500/10',
    subIcon: 'bg-primary-500/20 text-primary-400',
    navActive: 'bg-primary-600 text-white',
    navDot: 'bg-primary-500',
  },
  indigo: {
    tag: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    bullet: 'text-indigo-400',
    subCard: 'border-indigo-500/20 hover:border-indigo-500/50 hover:shadow-indigo-500/10',
    subIcon: 'bg-indigo-500/20 text-indigo-400',
    navActive: 'bg-indigo-600 text-white',
    navDot: 'bg-indigo-500',
  },
  emerald: {
    tag: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    bullet: 'text-emerald-400',
    subCard: 'border-emerald-500/20 hover:border-emerald-500/50 hover:shadow-emerald-500/10',
    subIcon: 'bg-emerald-500/20 text-emerald-400',
    navActive: 'bg-emerald-600 text-white',
    navDot: 'bg-emerald-500',
  },
  orange: {
    tag: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    bullet: 'text-orange-400',
    subCard: 'border-orange-500/20 hover:border-orange-500/50 hover:shadow-orange-500/10',
    subIcon: 'bg-orange-500/20 text-orange-400',
    navActive: 'bg-orange-600 text-white',
    navDot: 'bg-orange-500',
  },
  rose: {
    tag: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    bullet: 'text-rose-400',
    subCard: 'border-rose-500/20 hover:border-rose-500/50 hover:shadow-rose-500/10',
    subIcon: 'bg-rose-500/20 text-rose-400',
    navActive: 'bg-rose-600 text-white',
    navDot: 'bg-rose-500',
  },
};

const Features = () => {
  const [activeModule, setActiveModule] = useState('inventory');
  const active = modules.find((m) => m.id === activeModule);
  const ac = accentClasses[active.accent];
  const ActiveIcon = active.icon;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors duration-200">
      <Navbar />

      {/* ── Hero ── */}
      <section className="pt-28 pb-20 relative overflow-hidden bg-white dark:bg-gray-950">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-primary-600 opacity-10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500 opacity-10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="inline-block px-4 py-1.5 bg-primary-500/20 text-primary-400 text-sm font-semibold rounded-full border border-primary-500/30 mb-6 tracking-widest uppercase">
            Full Feature Overview
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            One Platform,<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-indigo-400">
              Every Tool You Need
            </span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-10">
            StockWise is built for Ethiopian businesses that want to run their entire operation — inventory, orders, finance, and team — from a single cloud platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-trial" className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-2xl transition-all duration-200 shadow-lg shadow-primary-600/30">
              Start Free Trial
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-8 py-4 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 text-gray-800 dark:text-white font-semibold rounded-2xl border border-gray-300 dark:border-white/20 transition-all duration-200">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* ── Module Navigator ── */}
      <section className="sticky top-20 z-30 bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm border-b border-transparent dark:border-gray-800 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
            {modules.map((mod) => {
              const ModIcon = mod.icon;
              const isActive = mod.id === activeModule;
              const cls = accentClasses[mod.accent];
              return (
                <button
                  key={mod.id}
                  onClick={() => setActiveModule(mod.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap transition-all duration-200 ${
                    isActive ? cls.navActive : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10'
                  }`}
                >
                  <ModIcon className="w-4 h-4" />
                  {mod.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Active Module Detail ── */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Top banner */}
          <div className={`relative rounded-3xl bg-gradient-to-br ${active.color} p-10 md:p-14 mb-12 overflow-hidden`}>
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-white/5 rounded-full" />
            <div className="absolute -bottom-16 -left-16 w-80 h-80 bg-white/5 rounded-full" />
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4 bg-white/20 text-white`}>
                  {active.tagline}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                  {active.headline}
                </h2>
                <p className="text-white/75 text-lg leading-relaxed">
                  {active.description}
                </p>
              </div>
              <div className="flex items-center justify-center lg:justify-end">
                <div className="w-40 h-40 bg-white/10 rounded-3xl flex items-center justify-center backdrop-blur-sm border border-white/20">
                  <ActiveIcon className="w-20 h-20 text-white/80" />
                </div>
              </div>
            </div>
          </div>

          {/* Two-column layout: bullets + sub-feature cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">

            {/* Bullet list */}
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-3xl p-8">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">What's included</h3>
              <ul className="space-y-3">
                {active.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircleIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${ac.bullet}`} />
                    <span className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sub-feature cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {active.subFeatures.map((sf, i) => {
                const SfIcon = sf.icon;
                return (
                  <div
                    key={i}
                    className={`bg-white dark:bg-gray-900 border rounded-2xl p-6 transition-all duration-300 hover:shadow-xl ${ac.subCard}`}
                  >
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${ac.subIcon}`}>
                      <SfIcon className="w-5 h-5" />
                    </div>
                    <h4 className="text-gray-900 dark:text-white font-semibold mb-1">{sf.name}</h4>
                    <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{sf.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Module switcher pills */}
          <div className="flex flex-wrap gap-3 justify-center">
            {modules.filter((m) => m.id !== activeModule).map((mod) => {
              const ModIcon = mod.icon;
              return (
                <button
                  key={mod.id}
                  onClick={() => setActiveModule(mod.id)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-xl text-sm font-medium transition-all duration-200"
                >
                  <ModIcon className="w-4 h-4" />
                  Explore {mod.label}
                  <ArrowRightIcon className="w-3 h-3" />
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── All Modules Overview ── */}
      <section className="py-20 bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-50 dark:bg-white/5 text-primary-700 dark:text-gray-400 text-sm font-semibold rounded-full border border-primary-200 dark:border-gray-700 mb-4 uppercase tracking-widest">
              Complete Platform
            </span>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">All Modules at a Glance</h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
              Every module works independently but is deeply integrated — a sale automatically updates inventory and creates a finance entry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {modules.map((mod) => {
              const ModIcon = mod.icon;
              const cls = accentClasses[mod.accent];
              return (
                <button
                  key={mod.id}
                  onClick={() => {
                    setActiveModule(mod.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="group text-left bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-600 rounded-3xl p-8 transition-all duration-300 hover:shadow-xl"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${cls.subIcon} group-hover:scale-110 transition-transform duration-300`}>
                    <ModIcon className="w-6 h-6" />
                  </div>
                  <h3 className="text-gray-900 dark:text-white font-bold text-lg mb-2">{mod.label}</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">{mod.tagline}</p>
                  <ul className="space-y-1">
                    {mod.bullets.slice(0, 3).map((b, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cls.navDot}`} />
                        {b}
                      </li>
                    ))}
                    <li className="text-xs text-gray-400 dark:text-gray-600 pl-3.5">{mod.bullets.length - 3} more...</li>
                  </ul>
                </button>
              );
            })}

            {/* Telegram card */}
            <div className="group bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-[#2AABEE]/40 rounded-3xl p-8 transition-all duration-300 hover:shadow-xl">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300" style={{ background: 'rgba(42,171,238,0.15)' }}>
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#2AABEE">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
                </svg>
              </div>
              <h3 className="text-gray-900 dark:text-white font-bold text-lg mb-2">Telegram Notifications</h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Instant Alerts</p>
              <ul className="space-y-1">
                {['Low stock notifications', 'New order alerts', 'Payment confirmations'].map((b, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[#2AABEE]" />
                    {b}
                  </li>
                ))}
                <li className="text-xs text-gray-400 dark:text-gray-600 pl-3.5">& more...</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Ready to See It in Action?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-10">
            Start your free 7-day trial. No credit card required. Full access to all features.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/request-trial"
              className="inline-flex items-center gap-2 px-10 py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold text-lg rounded-2xl transition-all shadow-xl shadow-primary-600/30"
            >
              Start Free Trial
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-10 py-4 bg-white/10 hover:bg-white/15 text-white font-semibold text-lg rounded-2xl border border-white/20 transition-all"
            >
              View Pricing
            </Link>
          </div>
          <p className="mt-6 text-gray-500 text-sm">
            ✓ No credit card &nbsp;&nbsp; ✓ Setup in minutes &nbsp;&nbsp; ✓ Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;
